'use client';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import { X, ChevronLeft, ChevronRight, Fuel, ArrowRight } from 'lucide-react';
import type { Car } from '@/lib/supabase/dinauto-service';

interface CarDetailModalProps {
  car: Car;
  onClose: () => void;
  onReserve: (car: Car) => void;
}

export default function CarDetailModal({ car, onClose, onReserve }: CarDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'Exterior' | 'Interior'>('Exterior');
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Compute available tabs based on actual image labels
  const exteriorImages = car.images.filter((img) => img.type === 'Exterior');
  const interiorImages = car.images.filter((img) => img.type === 'Interior');
  const hasInterior = interiorImages.length > 0;

  // Touch swipe state
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  // Escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Filter images by active tab; if no exterior images fall back to all images
  const tabImages = activeTab === 'Exterior'
    ? (exteriorImages.length > 0 ? exteriorImages : car.images)
    : interiorImages;
  const filtered = tabImages;

  // Clamp selectedIndex to valid range for current filtered set
  const safeIndex = Math.min(selectedIndex, Math.max(0, filtered.length - 1));
  const activeImg = filtered[safeIndex] ?? car.images[0];

  const goTo = useCallback((idx: number) => {
    if (filtered.length === 0) return;
    setSelectedIndex(((idx % filtered.length) + filtered.length) % filtered.length);
  }, [filtered.length]);

  const prev = useCallback(() => goTo(safeIndex - 1), [goTo, safeIndex]);
  const next = useCallback(() => goTo(safeIndex + 1), [goTo, safeIndex]);

  const handleTabChange = (tab: 'Exterior' | 'Interior') => {
    setActiveTab(tab);
    setSelectedIndex(0);
  };

  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 40) {
      if (diff > 0) next();
      else prev();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <span className={
              car.category === 'Economy' ? 'badge-economy' :
              car.category === 'Comfort' ? 'badge-comfort' : 'badge-suv'
            }>{car.category}</span>
            <h2 className="font-black text-2xl mt-1" style={{ fontWeight: 900 }}>
              {car.brand} {car.model}{car.year ? ` ${car.year}` : ''}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted transition-colors" aria-label="Close">
            <X size={22} />
          </button>
        </div>

        <div className="grid lg:grid-cols-5 gap-0">
          {/* Gallery */}
          <div className="lg:col-span-3 p-6 border-r border-border">
            {/* Tab */}
            <div className="flex mb-4 border border-border">
              <button
                key="gallery-tab-Exterior"
                onClick={() => handleTabChange('Exterior')}
                className={`flex-1 py-2 text-sm font-700 transition-colors ${
                  activeTab === 'Exterior' ? 'bg-foreground text-white' : 'hover:bg-muted text-muted-foreground'
                }`}
              >
                Exterior
              </button>
              {hasInterior && (
                <button
                  key="gallery-tab-Interior"
                  onClick={() => handleTabChange('Interior')}
                  className={`flex-1 py-2 text-sm font-700 transition-colors ${
                    activeTab === 'Interior' ? 'bg-foreground text-white' : 'hover:bg-muted text-muted-foreground'
                  }`}
                >
                  Interior
                </button>
              )}
            </div>

            {/* Main image with swipe support */}
            <div
              className="relative h-64 bg-muted overflow-hidden select-none"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {activeImg ? (
                <AppImage
                  key={activeImg.id}
                  src={activeImg.url}
                  alt={activeImg.alt || car.model}
                  fill
                  sizes="(max-width: 1024px) 100vw, 60vw"
                  className="object-cover"
                  priority
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                  No image available
                </div>
              )}
              {filtered.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); prev(); }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-foreground/70 text-white p-1.5 hover:bg-foreground transition-colors z-10"
                    aria-label="Previous"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); next(); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-foreground/70 text-white p-1.5 hover:bg-foreground transition-colors z-10"
                    aria-label="Next"
                  >
                    <ChevronRight size={18} />
                  </button>
                </>
              )}
              {/* Image counter */}
              {filtered.length > 1 && (
                <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded-sm">
                  {safeIndex + 1} / {filtered.length}
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {filtered.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
                {filtered.map((img, idx) => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => setSelectedIndex(idx)}
                    className={`shrink-0 w-20 h-14 border-2 overflow-hidden transition-all ${
                      idx === safeIndex ? 'border-primary' : 'border-border hover:border-foreground'
                    }`}
                    aria-label={`View image ${idx + 1}`}
                  >
                    <AppImage
                      src={img.url}
                      alt={img.alt || `${car.model} image ${idx + 1}`}
                      width={80}
                      height={56}
                      className="object-cover w-full h-full"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="lg:col-span-2 p-6 flex flex-col">
            <div className="flex items-end justify-between mb-5">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Price per day</p>
                <p className="text-3xl font-black tabular-nums" style={{ fontWeight: 900 }}>
                  {car.pricePerDay.toLocaleString('no-NO')} NOK
                </p>
              </div>
              <span className={`status-badge ${car.status === 'Available' ? 'status-available' : 'status-reserved'}`}>
                {car.status}
              </span>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed mb-5">{car.description}</p>

            <div className="space-y-3 mb-6">
              {[
                { label: 'Year', value: car.year ? String(car.year) : null },
                { label: 'Seats', value: `${car.seats} seats` },
                { label: 'Gearbox', value: car.gearbox },
                { label: 'Fuel', value: car.fuel },
                ...(car.drive ? [{ label: 'Drive', value: car.drive }] : []),
                ...(car.doors ? [{ label: 'Doors', value: String(car.doors) }] : []),
                ...(car.luggageCapacity ? [{ label: 'Luggage', value: `${car.luggageCapacity} L` }] : []),
                ...(car.hasAc !== undefined ? [{ label: 'A/C', value: car.hasAc ? 'Yes' : 'No' }] : []),
                { label: 'Brand', value: car.brand },
                { label: 'Category', value: car.category },
              ].filter((s) => s.value !== null).map((spec) => (
                <div key={`spec-${spec.label}`} className="flex justify-between items-center py-2 border-b border-border text-sm">
                  <span className="text-muted-foreground font-500">{spec.label}</span>
                  <span className="font-700">{spec.value}</span>
                </div>
              ))}
            </div>

            {/* Trust */}
            <div className="bg-muted p-3 mb-5 text-xs text-muted-foreground space-y-1">
              <p>✓ Free cancellation up to 24h before pickup</p>
              <p>✓ No hidden fees — price shown is final</p>
              <p>✓ 7+ days: 10% off · 14+ days: 15% off</p>
            </div>

            <button
              className={`btn-primary w-full justify-center gap-2 ${car.status === 'Reserved' ? 'opacity-40 cursor-not-allowed' : ''}`}
              disabled={car.status === 'Reserved'}
              onClick={() => { if (car.status !== 'Reserved') { onReserve(car); onClose(); } }}
            >
              Reserve This Car <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}